// App entry (Brief 4 / Brief 5). Two product surfaces over the real transport:
//   - the Dashboard (browser): the home for a SIGNED-IN orchestrator. A
//     Discord-style left→right descent — the sidebar (the Global room + your
//     projects) → a project's sessions → a session's operators + live tail.
//   - the desktop mini-app: Waytide's per-session chat channels for the small
//     floating Tauri window, served at "/" inside the desktop shell.
//
// The web's PUBLIC front door is the landing page (product + downloads) — a new
// visitor lands there, downloads the desktop app (the core input), and signs in
// only via the account button (→ /signin). No site-wide access code.

import {
  BrowserRouter,
  HashRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { isDesktopShell } from "./modules/desktop/desktopBridge";
import {
  OrchestratorSignIn,
  useOrchestratorAuth,
} from "./modules/auth/OrchestratorSignIn";
import { DashboardShell } from "./modules/shell/DashboardShell";
import { PresenceProvider } from "@waytide/ui";
import { ProjectsProvider } from "./modules/dashboard/projects/ProjectsContext";
import { DashboardHome } from "./modules/dashboard/DashboardHome";
import { AdherenceInsight } from "./modules/dashboard/insight/AdherenceInsight";
import { CorpusView } from "./modules/dashboard/corpus/CorpusView";
import { PrDesk } from "./modules/dashboard/prdesk/PrDesk";
import { GlobalRoom } from "./modules/dashboard/global/GlobalRoom";
import { ProjectSessions } from "./modules/dashboard/sessions/ProjectSessions";
import { SessionView } from "./modules/dashboard/session/SessionView";
import { GovernPage } from "./modules/govern/GovernPage";
import { RadioPage } from "./modules/radio/RadioPage";
import { ChannelsPage } from "@waytide/ui";
import { EvidencePage } from "./modules/trace/EvidencePage";
import { ShareLandingPage } from "./modules/share/ShareLandingPage";
import { DesktopDownloadPage } from "./modules/public/DesktopDownloadPage";
import { LandingPage } from "./modules/public/LandingPage";
import { DeepLinkBridge } from "./modules/desktop/useDeepLink";
import { VersionBadge } from "./modules/observability/VersionBadge";
import { installConsoleCapture } from "./modules/observability/logBuffer";

// Mirror console into the in-app log buffer as early as possible, so the
// badge's logs modal captures the whole session (Brief 5: build stamp + logs
// on every screen, every Waytide app).
installConsoleCapture();

/** The desktop product surface (Brief 4 / P6): the per-session chat channels
 * mini-app, rendered from @waytide/ui. Since the fold-in the PRIMARY desktop build
 * is apps/desktop (its own lean frontend wires the Tauri machine-id + bridge +
 * Connect flow into <ChannelsPage>). In the BROWSER (here) the miniapp is a
 * review surface: machine-scoping is off (show all sessions), the bridge is inert
 * ("idle"), and there is no Connect flow (a browser has no local bridge). This
 * also remains the safe fallback frontend if the desktop ever points back at
 * web/dist. In the Tauri shell it fills the window; in a browser it sits in a
 * compact framed window so the small-window proportions show during review. */
function MiniappSurface({ desktop }: { desktop: boolean }) {
  if (desktop) {
    return (
      <div className="h-dvh w-screen overflow-hidden">
        <ChannelsPage machineId={null} bridgeStatus="idle" />
      </div>
    );
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--waytide-sys-surface-ambient)] p-6">
      <div className="h-[680px] w-[420px] overflow-hidden">
        <ChannelsPage machineId={null} bridgeStatus="idle" />
      </div>
    </div>
  );
}

export function ShellRoutes({ desktop }: { desktop: boolean }) {
  return (
    // One live presence subscription + one projects list for the whole
    // Dashboard — drives the sidebar's needs-me badges and the tab-title Notice
    // signal (reaches you on every gated surface).
    <PresenceProvider>
      <ProjectsProvider>
        <Routes>
          {/* In a browser the Dashboard IS what a signed-in orchestrator lands
              on. The desktop mini-app (the per-session channels) is the DESKTOP
              product and survives at "/" only inside the Tauri shell. */}
          <Route
            path="/"
            element={
              desktop ? (
                <MiniappSurface desktop={desktop} />
              ) : (
                // Signed in, in a browser: "/" still shows the PUBLIC landing (its
                // sign-in CTA becomes a Dashboard link); the app lives at /dashboard.
                <LandingPage />
              )
            }
          />
          <Route element={<DashboardShell />}>
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/dashboard/adherence" element={<AdherenceInsight />} />
            <Route path="/dashboard/corpus" element={<CorpusView />} />
            <Route path="/dashboard/pull-requests" element={<PrDesk />} />
            <Route path="/dashboard/global" element={<GlobalRoom />} />
            <Route
              path="/dashboard/p/:projectId"
              element={<ProjectSessions />}
            />
            <Route path="/dashboard/s/:threadId" element={<SessionView />} />
            <Route path="/govern" element={<GovernPage />} />
            <Route path="/radio" element={<RadioPage />} />
            <Route
              path="/channels"
              element={<ChannelsPage machineId={null} bridgeStatus="idle" />}
            />
            <Route path="/trace/:project" element={<EvidencePage />} />
          </Route>
          {/* The retired "fleet" route still resolves — old links land home. A
              signed-in user hitting the landing/sign-in routes falls through here
              too (→ "/" → dashboard). */}
          <Route path="/fleet" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ProjectsProvider>
    </PresenceProvider>
  );
}

/** The DESKTOP (Tauri) shell — straight to sign-in, then the mini-app. A desktop
 * user signs in once; there's no public marketing surface inside the app. */
function DesktopShellEntry() {
  const orchestrator = useOrchestratorAuth();
  if (!orchestrator.signedIn) {
    return <OrchestratorSignIn onSignedIn={orchestrator.refresh} />;
  }
  return <ShellRoutes desktop />;
}

/** The BROWSER entry. Signed in → the dashboard. Signed out → the PUBLIC landing
 * (product + downloads) at "/", with sign-in behind the account button at
 * "/signin". Nothing else is exposed to a signed-out visitor. */
function WebEntry() {
  const orchestrator = useOrchestratorAuth();
  if (orchestrator.signedIn) {
    return <ShellRoutes desktop={false} />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/signin"
        element={<OrchestratorSignIn onSignedIn={orchestrator.refresh} />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  const desktop = isDesktopShell();
  // HashRouter in the Tauri shell (file:// origin has no history server);
  // BrowserRouter in the browser.
  const Router = desktop ? HashRouter : BrowserRouter;

  return (
    <Router>
      {/* P6: route an opened waytide:// deep link to its channel (desktop only;
          no-op in the browser). Inside the router so it can navigate. */}
      <DeepLinkBridge />
      <Routes>
        {/* The share landing (Brief 3b): backend-minted <origin>/c/<token>
            links, the no-install web lane. Public — the token is the guest's
            auth, on its own in-memory session, never the orchestrator's. */}
        <Route path="/c/:token" element={<ShareLandingPage />} />
        {/* Get-the-apps. Public; the landing's downloads link here too. */}
        <Route path="/download" element={<DesktopDownloadPage />} />
        <Route
          path="*"
          element={desktop ? <DesktopShellEntry /> : <WebEntry />}
        />
      </Routes>
      {/* The build stamp + logs modal, on every screen (signed in or not). */}
      <VersionBadge />
    </Router>
  );
}
